import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, LogOut, Moon, Sun, UserCog } from 'lucide-react';
import apiClient from '../api/client';
import { clearAuthSession, getAuthSession, type MeResponse } from '../services/auth';
import { useTheme } from '../hooks/useTheme';

/**
 * 个人设置：账号信息（/auth/me）、所属租户与角色、外观主题、退出登录。
 * Facet 模式下账号资料由统一身份中心管理，此处只读展示。
 */
export default function Profile() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [session] = useState(getAuthSession);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get<MeResponse>('/auth/me')
      .then((res) => {
        if (!cancelled) setMe(res.data);
      })
      .catch(() => {
        if (!cancelled) setError('账号信息加载失败，请稍后重试');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const logout = () => {
    clearAuthSession();
    window.location.href = '/login';
  };

  const displayName = me?.display_name || session?.userName || '用户';

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
        <UserCog size={20} />
        个人设置
      </h1>

      {loading ? (
        <div className="card" style={{ padding: 24, textAlign: 'center' }}>
          <Loader2 size={20} className="spin" />
          <span style={{ marginLeft: 8 }}>加载账号信息...</span>
        </div>
      ) : error ? (
        <div className="card" style={{ padding: 24, color: 'var(--color-danger, #e5534b)' }}>{error}</div>
      ) : (
        <>
          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  background: 'var(--gradient-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  fontWeight: 600,
                  color: 'white',
                }}
              >
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 600 }}>{displayName}</div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{me?.email}</div>
              </div>
            </div>
          </div>

          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>所属租户</div>
            {me && me.tenants.length > 0 ? (
              <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ color: 'var(--text-muted)', textAlign: 'left' }}>
                    <th style={{ padding: '6px 8px', fontWeight: 500 }}>租户</th>
                    <th style={{ padding: '6px 8px', fontWeight: 500 }}>标识</th>
                    <th style={{ padding: '6px 8px', fontWeight: 500 }}>角色</th>
                  </tr>
                </thead>
                <tbody>
                  {me.tenants.map((t) => (
                    <tr key={t.tenant_id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                      <td style={{ padding: '8px' }}>{t.name}</td>
                      <td style={{ padding: '8px', color: 'var(--text-muted)' }}>{t.slug}</td>
                      <td style={{ padding: '8px' }}>{t.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>暂无租户信息</div>
            )}
          </div>

          <div className="card" style={{ padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>外观</div>
            <button type="button" className="secondary" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
              <span style={{ marginLeft: 6 }}>{theme === 'dark' ? '切换到浅色模式' : '切换到深色模式'}</span>
            </button>
          </div>

          <div className="card" style={{ padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>会话</div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" className="secondary" onClick={() => navigate(-1)}>
                返回上一页
              </button>
              <button type="button" className="danger" onClick={logout}>
                <LogOut size={14} />
                <span style={{ marginLeft: 6 }}>退出登录</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
